import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Package, ArrowDown, ArrowUp, RefreshCw, AlertTriangle, History, X, Box } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost } from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/format';

const inputCls =
  'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#1F7A6E] focus:ring-1 focus:ring-[#1F7A6E] focus:outline-none';

const KIND_META = {
  GRN:           { color: 'bg-emerald-50 text-emerald-700', icon: ArrowDown,  label: 'Goods Received' },
  Issue:         { color: 'bg-rose-50 text-rose-700',       icon: ArrowUp,    label: 'Issued' },
  Return:        { color: 'bg-teal-50 text-[#1F7A6E]',    icon: ArrowDown,  label: 'Returned' },
  'Transfer In': { color: 'bg-emerald-50 text-emerald-700', icon: ArrowDown,  label: 'Transfer In' },
  'Transfer Out':{ color: 'bg-teal-50 text-[#1F7A6E]',    icon: ArrowUp,    label: 'Transfer Out' },
  Adjustment:    { color: 'bg-slate-100 text-slate-700',    icon: RefreshCw,  label: 'Adjustment' },
  Wastage:       { color: 'bg-rose-50 text-rose-700',       icon: ArrowUp,    label: 'Wastage' },
};

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState({});
  const [tab, setTab] = useState('items');
  const [loading, setLoading] = useState(true);
  const [movementForm, setMovementForm] = useState(null);
  const [ledgerItem, setLedgerItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [it, mv, sm] = await Promise.all([
        apiGet('/inventory-items'),
        apiGet('/inventory/movements?limit=200'),
        apiGet('/inventory/summary'),
      ]);
      setItems(it);
      setMovements(mv);
      setSummary(sm);
    } catch { toast.error('Load failed'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader
        overline="Inventory"
        title="Material & Stores"
        subtitle="Live stock ledger · GRN, issue, return, adjustment workflows · low-stock alerts"
        actions={
          <button data-testid="inventory-issue-btn" onClick={() => setMovementForm({ kind: 'Issue' })}
            className="flex items-center gap-2 px-4 py-2 bg-[#1F7A6E] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#14504E] transition-all">
            <ArrowUp size={15} /> Issue Material
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="SKUs" value={summary.items_count ?? '—'} icon={Package} />
        <Stat label="Low Stock" value={summary.low_stock_count ?? 0} icon={AlertTriangle} accent={summary.low_stock_count ? 'text-rose-700' : ''} />
        <Stat label="Movements Today" value={summary.movements_today ?? 0} icon={History} />
        <Stat label="Total Qty (mixed)" value={summary.total_qty ?? 0} icon={Box} />
      </div>

      <div className="border-b border-slate-200 mb-5 flex items-center gap-1">
        {['items', 'movements', 'low-stock'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`inventory-tab-${t}`}
            className={`px-4 py-2 text-sm font-display font-semibold capitalize border-b-2 -mb-px transition-colors ${tab === t ? 'border-[#1F7A6E] text-[#1F7A6E]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t === 'items' ? `SKUs · ${items.length}` : t === 'movements' ? `Movements · ${movements.length}` : `Low Stock · ${summary.low_stock_count || 0}`}
          </button>
        ))}
      </div>

      {tab === 'items' && (
        <ItemsTable items={items} loading={loading} onMovement={(it, kind) => setMovementForm({ kind, item_id: it.id, item: it })} onLedger={setLedgerItem} />
      )}
      {tab === 'movements' && <MovementsTable movements={movements} loading={loading} />}
      {tab === 'low-stock' && (
        <ItemsTable items={summary.low_stock_items || []} loading={loading} onMovement={(it, kind) => setMovementForm({ kind, item_id: it.id, item: it })} onLedger={setLedgerItem} />
      )}

      {movementForm && (
        <MovementModal
          initial={movementForm}
          items={items}
          onClose={() => setMovementForm(null)}
          onSaved={() => { setMovementForm(null); load(); }}
        />
      )}
      {ledgerItem && <LedgerDrawer item={ledgerItem} onClose={() => setLedgerItem(null)} />}
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
      {Icon && <Icon size={20} className="text-slate-400" />}
      <div>
        <div className="kpi-overline mb-0.5">{label}</div>
        <div className={`font-display font-extrabold text-2xl tabular-nums ${accent || 'text-[#0F172A]'}`}>{value}</div>
      </div>
    </div>
  );
}

function ItemsTable({ items, loading, onMovement, onLedger }) {
  if (loading) return <div className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">Loading…</div>;
  if (!items.length) return <div className="text-center py-12 border border-dashed border-slate-200 rounded-sm text-slate-400 text-xs uppercase tracking-widest font-display">No items</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto overflow-y-auto max-h-[70vh]">
      <table className="w-full text-sm sticky-col-header">
        <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
          <tr>
            <th className="px-4 py-2.5 text-left">Code</th>
            <th className="px-4 py-2.5 text-left">Name</th>
            <th className="px-4 py-2.5 text-right">Stock</th>
            <th className="px-4 py-2.5 text-left">Unit</th>
            <th className="px-4 py-2.5 text-right">Reorder</th>
            <th className="px-4 py-2.5 text-right">Rate</th>
            <th className="px-4 py-2.5 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => {
            const isLow = (it.reorder_level || 0) > 0 && (it.stock || 0) <= it.reorder_level;
            return (
              <tr key={it.id} data-testid={`inv-row-${it.id}`} className={`border-b border-slate-100 hover:bg-slate-50/40 ${isLow ? 'bg-red-50/30' : ''}`}>
                <td className="px-4 py-2.5 font-mono text-xs">{it.code}</td>
                <td className="px-4 py-2.5">{it.name}</td>
                <td className={`px-4 py-2.5 text-right tabular-nums font-display font-bold ${isLow ? 'text-rose-700' : ''}`}>{Number(it.stock || 0).toLocaleString('en-IN')}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{it.unit}</td>
                <td className="px-4 py-2.5 text-right text-xs text-slate-500 tabular-nums">{it.reorder_level || '—'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-xs">{it.rate ? `₹${Number(it.rate).toLocaleString('en-IN')}` : '—'}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => onMovement(it, 'Issue')} data-testid={`inv-issue-${it.id}`}
                      className="px-2 py-1 text-[10px] font-display font-semibold border border-slate-300 hover:border-[#1F7A6E] hover:text-[#1F7A6E] rounded-sm">ISSUE</button>
                    <button onClick={() => onMovement(it, 'Return')} data-testid={`inv-return-${it.id}`}
                      className="px-2 py-1 text-[10px] font-display font-semibold border border-slate-300 hover:border-[#1F7A6E] hover:text-[#1F7A6E] rounded-sm">RETURN</button>
                    <button onClick={() => onMovement(it, 'GRN')} data-testid={`inv-grn-${it.id}`}
                      className="px-2 py-1 text-[10px] font-display font-semibold border border-slate-300 hover:border-emerald-600 hover:text-emerald-700 rounded-sm">GRN</button>
                    <button onClick={() => onLedger(it)} data-testid={`inv-ledger-${it.id}`}
                      className="px-2 py-1 text-[10px] font-display font-semibold border border-slate-300 hover:border-slate-700 hover:text-slate-700 rounded-sm">LEDGER</button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MovementsTable({ movements, loading }) {
  if (loading) return <div className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">Loading…</div>;
  if (!movements.length) return <div className="text-center py-12 border border-dashed border-slate-200 rounded-sm text-slate-400 text-xs uppercase tracking-widest font-display">No movements yet — issue or receive material to start the ledger</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto overflow-y-auto max-h-[70vh]">
      <table className="w-full text-sm sticky-col-header">
        <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
          <tr>
            <th className="px-4 py-2.5 text-left">When</th>
            <th className="px-4 py-2.5 text-left">Item</th>
            <th className="px-4 py-2.5 text-left">Kind</th>
            <th className="px-4 py-2.5 text-right">Qty</th>
            <th className="px-4 py-2.5 text-right">Stock After</th>
            <th className="px-4 py-2.5 text-left">Reference / To</th>
            <th className="px-4 py-2.5 text-left">User</th>
          </tr>
        </thead>
        <tbody>
          {movements.map(m => {
            const meta = KIND_META[m.kind] || KIND_META.Adjustment;
            const Icon = meta.icon;
            return (
              <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/40">
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-500">{formatDate(m.at)}</td>
                <td className="px-4 py-2.5">
                  <div className="font-mono text-xs text-slate-700">{m.item_code}</div>
                  <div className="text-[11px] text-slate-500">{m.item_name}</div>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest ${meta.color}`}>
                    <Icon size={11} /> {m.kind}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-display font-bold">{m.direction === 'out' ? '−' : '+'}{Number(m.qty).toLocaleString('en-IN')} <span className="text-[10px] text-slate-400">{m.unit}</span></td>
                <td className="px-4 py-2.5 text-right tabular-nums text-xs text-slate-600">{Number(m.stock_after).toLocaleString('en-IN')}</td>
                <td className="px-4 py-2.5 text-xs text-slate-600">
                  {m.reference && <div className="font-mono">{m.reference}</div>}
                  {m.issued_to && <div>To: {m.issued_to}</div>}
                  {m.remarks && <div className="text-slate-400 italic">{m.remarks}</div>}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{m.user || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MovementModal({ initial, items, onClose, onSaved }) {
  const [form, setForm] = useState({
    kind: initial.kind,
    item_id: initial.item_id || '',
    qty: '',
    issued_to: '',
    reference: '',
    remarks: '',
  });
  const [busy, setBusy] = useState(false);

  const item = items.find(i => i.id === form.item_id);
  const isOut = ['Issue', 'Wastage', 'Transfer Out'].includes(form.kind);
  const isIn = ['GRN', 'Return', 'Transfer In'].includes(form.kind);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.item_id) { toast.error('Pick an item'); return; }
    if (!form.qty || Number(form.qty) <= 0) { toast.error('Quantity must be > 0'); return; }
    setBusy(true);
    try {
      const endpoint = form.kind === 'Issue' ? '/inventory/issue' :
                       form.kind === 'Return' ? '/inventory/return' :
                       form.kind === 'GRN' ? '/inventory/grn' :
                       '/inventory/adjust';
      const payload = {
        item_id: form.item_id,
        qty: Number(form.qty),
        issued_to: form.issued_to || undefined,
        reference: form.reference || undefined,
        remarks: form.remarks || undefined,
      };
      if (endpoint === '/inventory/adjust') payload.kind = form.kind;
      const r = await apiPost(endpoint, payload);
      toast.success(`${form.kind} recorded · stock now ${r.stock_after}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-lg" data-testid="inv-movement-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">{KIND_META[form.kind]?.label || form.kind}</div>
            <h3 className="font-display font-bold text-xl mt-1">Stock Movement</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Movement Type</label>
            <div className="flex flex-wrap gap-2">
              {['GRN', 'Issue', 'Return', 'Adjustment', 'Wastage'].map(k => (
                <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })}
                  data-testid={`inv-kind-${k.toLowerCase()}`}
                  className={`px-3 py-1.5 text-xs font-display font-semibold border rounded-sm transition-all ${form.kind === k ? 'border-[#1F7A6E] bg-teal-50 text-[#1F7A6E]' : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}>
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Item</label>
            <select value={form.item_id} onChange={e => setForm({ ...form, item_id: e.target.value })}
              data-testid="inv-form-item" className={`${inputCls} bg-white`}>
              <option value="">— Pick a SKU —</option>
              {items.map(i => <option key={i.id} value={i.id}>{i.code} · {i.name} (stock: {i.stock} {i.unit})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Qty {item && <span className="text-slate-400 font-mono">({item.unit})</span>}</label>
            <input type="number" min="0" step="0.01" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })}
              data-testid="inv-form-qty" className={`${inputCls} tabular-nums`} required />
            {isOut && item && Number(form.qty) > Number(item.stock) && (
              <div className="text-[10px] text-rose-700 mt-1 font-display">⚠ Exceeds available stock ({item.stock})</div>
            )}
          </div>
          {isOut && (
            <div className="col-span-2">
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Issued To (worker / sub-contractor / WO)</label>
              <input value={form.issued_to} onChange={e => setForm({ ...form, issued_to: e.target.value })}
                data-testid="inv-form-issued-to" className={inputCls} placeholder="e.g. M/s Sharma Constructions / WO-2025-014" />
            </div>
          )}
          <div className="col-span-2">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Reference (PO / GRN / Slip No.)</label>
            <input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })}
              data-testid="inv-form-reference" className={inputCls} placeholder="e.g. PO-2025-101" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Remarks</label>
            <textarea value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} rows={2}
              data-testid="inv-form-remarks" className={inputCls} />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-100 font-display">Cancel</button>
          <button type="submit" disabled={busy} data-testid="inv-form-submit"
            className="px-4 py-2 text-sm bg-[#1F7A6E] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {busy ? 'Recording…' : `Record ${form.kind}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function LedgerDrawer({ item, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiGet(`/inventory/${item.id}/ledger`).then(setData).catch(() => toast.error('Failed to load ledger'));
  }, [item.id]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end">
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-2xl flex flex-col" data-testid="inv-ledger-drawer">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">Ledger · {item.code}</div>
            <h3 className="font-display font-bold text-xl mt-1">{item.name}</h3>
            <div className="text-xs text-slate-500 mt-1">Current stock: <b className="text-[#0F172A]">{item.stock} {item.unit}</b></div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {!data ? (
            <div className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">Loading ledger…</div>
          ) : data.movements.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No movements recorded yet</div>
          ) : (
            <div className="space-y-2">
              {data.movements.slice().reverse().map(m => {
                const meta = KIND_META[m.kind] || KIND_META.Adjustment;
                const Icon = meta.icon;
                return (
                  <div key={m.id} className="border-l-2 border-slate-200 pl-4 py-2 hover:border-[#1F7A6E]/50">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest ${meta.color}`}>
                        <Icon size={11} /> {m.kind}
                      </span>
                      <span className="text-xs text-slate-500 font-mono">{formatDate(m.at)}</span>
                    </div>
                    <div className="font-display font-semibold text-sm tabular-nums">
                      {m.direction === 'out' ? '−' : '+'}{m.qty} {m.unit}
                      <span className="text-slate-400 font-normal ml-2">→ {m.stock_after} {m.unit}</span>
                    </div>
                    {(m.reference || m.issued_to || m.remarks) && (
                      <div className="text-xs text-slate-500 mt-1">
                        {m.reference && <span className="mr-3">Ref: <b className="font-mono">{m.reference}</b></span>}
                        {m.issued_to && <span className="mr-3">To: <b>{m.issued_to}</b></span>}
                        {m.remarks && <span className="italic">"{m.remarks}"</span>}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 mt-0.5 font-display">By {m.user || 'system'}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
